'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var clsx = require('clsx');
var nthBitHeader = require('@narvar/nth-bit-header');
var Button = require('@narvar/nth-block-button');
var TextInput = require('@narvar/nth-block-text-input');
var TextLink = require('@narvar/nth-bit-text-link');
var StarRating = require('@narvar/nth-block-star-rating');
var nthHookTheme = require('@narvar/nth-hook-theme');
var nthHookTranslations = require('@narvar/nth-hook-translations');
var LandscapeCollapsed_module = require('./LandscapeCollapsed.module.css.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var clsx__default = /*#__PURE__*/_interopDefaultLegacy(clsx);
var Button__default = /*#__PURE__*/_interopDefaultLegacy(Button);
var TextInput__default = /*#__PURE__*/_interopDefaultLegacy(TextInput);
var TextLink__default = /*#__PURE__*/_interopDefaultLegacy(TextLink);
var StarRating__default = /*#__PURE__*/_interopDefaultLegacy(StarRating);

const LandscapeCollapsed = ({
  featureName,
  selectedRating,
  onRatingSelect,
  submitFeedback,
  commentOptional,
  enablePanelHeader
}) => {
  const {
    feedbackButtonText = '',
    feedbackButtonTextShort = '',
    feedbackChangeStarRating = '',
    feedbackCommentLabel = '',
    feedbackExperienceHeader = ''
  } = nthHookTranslations.useTranslations();
  const {
    linksNoneDefault: textLinkStyle,
    linksNoneHover: textLinkHoverStyle
  } = nthHookTheme.useTheme();
  const [feedbackComment, setFeedbackComment] = React.useState('');
  const [collapsed, setCollapsed] = React.useState(true);
  const [interactiveExperienceHeader, setInteractiveExperienceHeader] = React.useState(feedbackExperienceHeader);

  /**
   * In this view of the feedback panel, we show the stars with the rest of the form hidden.
   * Keyboard events on the star radio inputs fire click events as well. We happen to have a
   * click handler on the star radio inputs that will cause the layout to move on to the comment.
   * This means when user uses the arrow keys to select a star rating, if we do nothing else, a click
   * event will fire with the first arrow key and the user will be taken to the comment section. We
   * don't want that. So we set a flag to check if the event was fired from a keydown event and if it
   * was, we bypass the click event. preventDefault on keydown does not work here because that would
   * cancel moving from one radio input to the next with the arrow keys. stopPropagation does not work
   * because that only prevents the event from being consumed by parent events. stomImmediatePropagation
   * does not work because that only prevents successive keydown event handlers defined on the element
   * itself from being called.
   */
  const firedFromKeydown = React.useRef(false);
  return /*#__PURE__*/React__default["default"].createElement(React__default["default"].Fragment, null, /*#__PURE__*/React__default["default"].createElement("div", {
    className: LandscapeCollapsed_module["default"].landscapeCollapsedContainer
  }, /*#__PURE__*/React__default["default"].createElement("div", {
    className: clsx__default["default"](LandscapeCollapsed_module["default"].columnLandscapeCollapsed, LandscapeCollapsed_module["default"].textAlignLeft),
    style: {
      gridColumn: 'span 5'
    }
  }, /*#__PURE__*/React__default["default"].createElement(nthBitHeader.MediumHeader, {
    as: enablePanelHeader ? 'p' : 'h2',
    featureName: featureName,
    containerCustomStyle: {
      lineHeight: '1.5rem'
    }
  }, interactiveExperienceHeader ?? feedbackExperienceHeader)), /*#__PURE__*/React__default["default"].createElement("div", {
    className: clsx__default["default"](LandscapeCollapsed_module["default"].columnLandscapeCollapsed, LandscapeCollapsed_module["default"].textAlignRight),
    style: {
      gridColumn: 'span 7'
    }
  }, collapsed ? /*#__PURE__*/React__default["default"].createElement("div", {
    className: LandscapeCollapsed_module["default"].startRatingContainer
  }, /*#__PURE__*/React__default["default"].createElement(StarRating__default["default"]
  // we need 2 different handlers for a11y. keyboard
  // nav events will trigger a click and cause this
  // layout to move on to the comment section when we
  // don't want it to.
  , {
    onKeyDown: (e, index, selectedText) => {
      const keyCode = e.nativeEvent.code;
      if (keyCode === 'Enter' || keyCode === 'Space' || keyCode === 'Tab') {
        onRatingSelect(index);
        setCollapsed(false);
        setInteractiveExperienceHeader(selectedText ?? feedbackExperienceHeader);
      }
      firedFromKeydown.current = true;
    },
    onClick: (index, selectedText) => {
      if (firedFromKeydown.current) {
        firedFromKeydown.current = false;
        return;
      }
      onRatingSelect(index);
      setCollapsed(false);
      setInteractiveExperienceHeader(selectedText ?? feedbackExperienceHeader);
    },
    selectedRating: selectedRating,
    featureName: featureName,
    isLandscape: true,
    displayTextFirst: true
  })) : /*#__PURE__*/React__default["default"].createElement("div", {
    "data-test": "feedbacklLandscapeCollapsedView"
  }, /*#__PURE__*/React__default["default"].createElement("div", {
    className: LandscapeCollapsed_module["default"].landscapeCollapsedFormContainer
  }, /*#__PURE__*/React__default["default"].createElement("div", {
    className: LandscapeCollapsed_module["default"].columnLandscapeCollapsed,
    style: {
      gridColumn: 'span 6'
    }
  }, /*#__PURE__*/React__default["default"].createElement(TextInput__default["default"], {
    type: TextInput.SupportedInputType.text,
    id: "feedbackCommentBox",
    animateLabel: true,
    label: feedbackCommentLabel || '',
    featureName: featureName,
    onChange: event => setFeedbackComment(event.target.value)
  })), /*#__PURE__*/React__default["default"].createElement("div", {
    className: LandscapeCollapsed_module["default"].columnLandscapeCollapsed,
    style: {
      gridColumn: 'span 4'
    }
  }, /*#__PURE__*/React__default["default"].createElement(Button__default["default"], {
    disabled: !commentOptional && !feedbackComment.length,
    big: true,
    featureName: featureName,
    onClick: () => {
      if (submitFeedback) submitFeedback(feedbackComment);
    }
  }, feedbackButtonTextShort || feedbackButtonText)))))), !collapsed && /*#__PURE__*/React__default["default"].createElement("div", {
    className: LandscapeCollapsed_module["default"].columnLandscapeCollapsed
  }, /*#__PURE__*/React__default["default"].createElement("div", {
    className: LandscapeCollapsed_module["default"].columnChangeStarRating,
    style: {
      gridColumn: 'span 12'
    }
  }, /*#__PURE__*/React__default["default"].createElement(TextLink__default["default"], {
    linkStyle: {
      sDefault: textLinkStyle,
      sHover: textLinkHoverStyle
    },
    onClick: () => {
      setCollapsed(true);
      setInteractiveExperienceHeader(feedbackExperienceHeader);
    }
  }, feedbackChangeStarRating))));
};
var LandscapeCollapsed$1 = nthHookTranslations.withTranslationsProvider(nthHookTheme.withThemeProvider(LandscapeCollapsed, {
  themeTokens: ['linksNoneDefault', 'linksNoneHover']
}), {
  translationKeys: ['feedbackButtonText', 'feedbackButtonTextShort', 'feedbackChangeStarRating', 'feedbackCommentLabel', 'feedbackExperienceHeader']
});

exports["default"] = LandscapeCollapsed$1;
